import {
  ShoppingCart,
  Inventory,
  TrendingUp,
  Dashboard,
  Business,
  AccountTree,
  BarChart,
  Factory,
  Phone,
  AutoMode,
  Download,
  People,
  Hub,
  SmartToy,
  MonitorHeart,
  MenuBook,
  Campaign,
  Insights,
  TaskAlt,
  Code,
  ReportProblem,
} from '@mui/icons-material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { SvgIconComponent } from '@mui/icons-material';

export interface MenuItem {
  title: string;
  description: string;
  icon: SvgIconComponent;
  path: string;
  key: string; // 메뉴 접근 권한 체크용 식별자
  /**
   * 사이드바에서만 감춘다. 주소·라우트·권한은 그대로다.
   *
   * ⛔ 여기서 항목을 **지우면 안 된다.** 권한 설정 화면(UserManagementTab)이
   *    이 목록으로 권한 키를 만든다 — 지우는 순간 「전체 허용」을 누를 때
   *    그 키가 저장에서 빠지고, 되돌릴 화면도 사라진다.
   *    그래서 지우는 대신 감춘다.
   */
  hiddenInSidebar?: boolean;
  /**
   * 권한 판정에 쓸 **다른** 메뉴 키. 주면 이 항목은 그 메뉴의 권한을 그대로 따르고,
   * 권한 설정 화면(UserManagementTab 등)에는 따로 나오지 않는다.
   * 예: 평생수선 → 'claims' (대표 결정 D-3 — 클레임을 보는 사람이 평생수선도 본다. 백엔드도 같은 키)
   */
  permissionKey?: string;
}

export interface MenuSection {
  title: string;
  sidebarTitle: string;
  icon: SvgIconComponent;
  color: string;
  menus: MenuItem[];
}

export const menuSections: MenuSection[] = [
  {
    title: '📊 인사이트 & 분석',
    sidebarTitle: '인사이트 & 분석',
    icon: BarChart,
    color: '#9333ea',
    menus: [
      {
        title: '대시보드',
        description: '오늘 할 일과 팀 업무를 한 화면에서 봅니다',
        icon: Dashboard,
        path: '/dashboard',
        key: 'dashboard',
      },
      // [F12] '통계'(/statistics) 메뉴 제거 — 통계는 업무 페이지 내부 뷰로 통합. legacy 'statistics' 권한 키는 마이그레이션 없이 보존([C-37]).
      // 2026-09-21 '분석'(/analytics) 메뉴도 제거. legacy 'analytics' 권한 키는 같은 이유로 보존한다.
      //   ⛔ 화면 부품(AnalyticsDashboard)은 지우지 않았다 — 구매 관리의 /purchase/analytics 가 같은 것을 쓴다(Codex PASS1 [C-1]).
      //   ⛔ /api/statistics 와 components/statistics/* 도 그대로다 — 판매·재고·구매·생산 화면의 «통계» 탭이 쓴다.
    ],
  },
  {
    title: '🤖 AI(인공지능)',
    sidebarTitle: 'AI(인공지능)',
    icon: Hub,
    color: '#06b6d4',
    menus: [
      // 업무 위키 — 데이터관계·지식문서·파일함·AI어시스턴트의 새 입구.
      //
      // S11-2 (2026-08-16): 네 개를 사이드바에서 **감췄다.** 지우지 않았다.
      //   실측상 위키는 아직 그 화면들을 대체하지 못한다 —
      //   지식문서는 90일간 25건 수정됐고(위키 사본은 정본 0건), AI 어시스턴트는
      //   대화 46건, 데이터 관계는 관계 53건이 살아 있다. 위키에는 문서 작성·채팅·
      //   관계도 화면이 없다. 지웠다면 쓰던 기능이 사라진다.
      //   그래서 «메뉴만 줄이고 화면은 남긴다». 주소로도, 위키 홈에서도 갈 수 있다.
      {
        title: '업무 위키',
        description: '항목 하나로 모아 보기',
        icon: MenuBook,
        path: '/wiki/assistant',   // AI 어시스턴트가 첫 탭(대표 요청 2026-08-19). 문서·관계지도는 상단 탭으로.
        key: 'wiki',
      },
      {
        title: 'AI 어시스턴트',
        description: 'Claude 채팅',
        icon: SmartToy,
        path: '/ontology/chat',
        key: 'ontology-chat',
        hiddenInSidebar: true,   // 업무 위키 홈에서 연결한다 (S11-2)
      },
      {
        title: '모니터링',
        description: '프로액티브 알림',
        icon: MonitorHeart,
        path: '/ontology/monitoring',
        key: 'ontology-monitoring',
      },
      // Claude 가 만든 변경 요청서를 사람이 승인하는 화면(대표 요청 2026-10-05). 화면 자체는 로그인만 보고
      // 서버가 본인 요청서만 돌려준다 — 메뉴는 Claude 원가 요청을 쓸 수 있는 «판매» 권한을 따른다.
      {
        title: 'Claude 변경 요청',
        description: 'Claude 입력 승인',
        icon: TaskAlt,
        path: '/mcp-approvals',
        key: 'mcp-approvals',
        permissionKey: 'sales',
      },
      // [미팅 통합 2026-09-08] ERP 쪽 미팅 입구는 없앴다 — **인사(HR) 메뉴에서만** 들어간다(대표 지시).
      //
      // 2026-09-21 [C-13] 항목을 **되살렸다**(사이드바에는 계속 안 보인다).
      //   빼 두면 `/meeting-notes/:id`·`/new` 는 살아 있는데(지난 알림·인사 녹음 버튼이 가리킨다)
      //   **권한 화면에서 이 열쇠를 줄 수가 없다.** 키가 없는 계정은 그 주소에서 «접근 거부» 로 튕기고
      //   관리자가 화면에서 고칠 방법이 없다 — 이 파일 상단의 «지우지 말고 감춘다» 규칙이 그대로 적용된다.
      //   (2026-09-21 실측: 부분권한 7개 계정 모두 열쇠를 갖고 있어 지금 잠긴 사람은 0명. 신규 계정용 예방이다.)
      {
        title: '회의록',
        description: '인사 › 미팅에서 들어간다',
        icon: MenuBook,
        path: '/meeting-notes',
        key: 'meeting-notes',
        hiddenInSidebar: true,
      },
    ],
  },
  {
    title: '📦 재고 & 판매',
    sidebarTitle: '재고 & 판매',
    icon: Inventory,
    color: '#3b82f6',
    menus: [
      {
        title: '거래처',
        description: '고객·공급업체·판매채널 정보와 거래 상태를 관리합니다',
        icon: People,
        path: '/contacts',
        key: 'contacts',
      },
      {
        title: '재고',
        description: '품목별 현재고·가용재고와 부족·과다 품목을 확인합니다',
        icon: Inventory,
        path: '/inventory',
        key: 'inventory',
      },
      {
        title: '판매',
        description: '견적서·주문서와 판매 실적 · 상태별로 걸러 보고 엑셀로 내보냅니다',
        icon: TrendingUp,
        path: '/sales',
        key: 'sales',
      },
    ],
  },
  {
    title: '🏭 구매 & 생산',
    sidebarTitle: '구매 & 생산',
    icon: ShoppingCart,
    color: '#10b981',
    menus: [
      {
        title: '구매',
        description: '발주 계획부터 입고까지 구매 주문을 관리합니다',
        icon: ShoppingCart,
        path: '/purchase',
        key: 'purchase',
      },
      {
        title: '생산',
        description: '생산 주문과 작업 지시 · 단계별 진행을 확인합니다',
        icon: Factory,
        path: '/production',
        key: 'production',
      },
      {
        title: 'BOM',
        description: '완제품을 이루는 부품·원단 구성을 관리합니다',
        icon: AccountTree,
        path: '/bom',
        key: 'bom',
      },
    ],
  },
  {
    title: '👥 고객 관리',
    sidebarTitle: '고객 관리',
    icon: Business,
    color: '#ec4899',
    menus: [
      {
        title: '해피콜',
        description: '배송 뒤 고객 만족 확인 · 끌어 옮기면 상태가 바뀝니다',
        icon: Phone,
        path: '/happycall',
        key: 'happycall',
      },
      {
        title: '홈스타일링',
        description: '방문 상담부터 설치까지 · 끌어 옮기면 상태가 바뀝니다',
        icon: Business,
        path: '/home-styling',
        key: 'home-styling',
      },
      {
        title: '설치일정',
        description: '판매주문서 기반 설치기사 배정 및 일정 관리',
        icon: CalendarMonthIcon,
        path: '/installation-scheduling',
        key: 'installation-scheduling',
      },
      {
        title: '클레임',
        description: '교환·반품·수선 접수부터 처리 완료까지',
        icon: ReportProblem,
        path: '/claims',
        key: 'claims',
      },
      {
        title: '평생수선',
        description: '평생수선 신청 접수부터 수선 완료까지 · 끌어 옮기면 상태가 바뀝니다',
        icon: TaskAlt,
        path: '/repair-requests',
        key: 'repair-requests',
        permissionKey: 'claims',
      },
    ],
  },
  {
    title: '📣 마케팅 & 정산',
    sidebarTitle: '마케팅 & 정산',
    icon: Campaign,
    color: '#f43f5e',
    menus: [
      // ⚠️ '광고성과'를 첫 번째로 유지한다 — 접힌 사이드바에서 그룹을 클릭하면
      //    첫 항목으로 이동하므로, 순서를 바꾸면 기존 사용자의 목적지가 조용히 바뀐다.
      {
        title: '광고성과',
        description: '광고비·ROAS 분석',
        icon: Insights,
        path: '/marketing/ad-performance',
        key: 'marketing-ad',
      },
      {
        title: '채널별 손익',
        description: '매출·원가·수수료·광고비 차감 후 실질 이익',
        icon: Insights,
        path: '/marketing/channel-pnl',
        key: 'marketing-channel-pnl',
      },
    ],
  },
  {
    title: '⚙️ 자동화',
    sidebarTitle: '자동화',
    icon: AutoMode,
    color: '#f59e0b',
    menus: [
      {
        title: '주문수집',
        description: '판매채널 주문을 자동으로 모아 판매 주문으로 옮깁니다',
        icon: Download,
        path: '/automation/order-collection',
        key: 'automation-orders',
      },
      {
        title: '자동화 태스크',
        description: '정해진 시각에 도는 자동화 작업의 상태와 실행 기록',
        icon: TaskAlt,
        path: '/automation/tasks',
        key: 'automation-tasks',
      },
    ],
  },
];

/** 다른 메뉴의 권한을 따르는 항목 — { 메뉴 키: 따르는 권한 키 }. 판정은 utils/menuAccess 가 쓴다 */
export const PERMISSION_KEY_ALIASES: Record<string, string> = Object.fromEntries(
  menuSections.flatMap((s) => s.menus).filter((m) => m.permissionKey).map((m) => [m.key, m.permissionKey as string]),
);
